<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type Keycloak from 'keycloak-js'
import { CheckIcon, ChevronRightIcon, MenuIcon } from 'lucide-vue-next'
import CompanySwitcher from '@/components/layout/CompanySwitcher.vue'
import Menu from '@/components/ui/Menu.vue'
import Tag from '@/components/ui/Tag.vue'
import { useTheme, type ThemePreference } from '@/composables/useTheme'
import { useAppStore } from '@/stores/app'
import { initials } from '@/util/initials'
import type { UserFunctions } from '@/plugins/keycloak'

const emit = defineEmits<{ toggleSidebar: [] }>()

const { t } = useI18n()
const router = useRouter()
const appStore = useAppStore()
const keycloak = inject<Keycloak>('keycloak')
const userFunctions = inject<UserFunctions>('userFunctions')
const { preference, setPreference } = useTheme()

const userMenu = ref<InstanceType<typeof Menu> | null>(null)

const userName = computed(() => keycloak?.tokenParsed?.name ?? '')
const userEmail = computed(() => keycloak?.tokenParsed?.email ?? '')
const isAdmin = computed(() => userFunctions?.isAdmin() ?? false)

const THEMES: { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'layout.topbar.themeSystem' },
  { value: 'light', label: 'layout.topbar.themeLight' },
  { value: 'dark', label: 'layout.topbar.themeDark' }
]
const LOCALES = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' }
]

const logout = () => {
  keycloak?.logout({ redirectUri: 'https://www.mobidic.it' })
}

const menuItems = computed(() => [
  {
    label: t('layout.topbar.account'),
    items: [
      { key: 'profile', label: t('layout.menu.profile'), command: () => router.push({ name: 'profile' }) },
      { key: 'settings', label: t('layout.menu.settings'), command: () => router.push({ name: 'settings' }) }
    ]
  },
  {
    label: t('layout.topbar.theme'),
    items: THEMES.map((theme) => ({
      key: `theme-${theme.value}`,
      label: t(theme.label),
      checked: preference.value === theme.value,
      command: () => setPreference(theme.value)
    }))
  },
  {
    label: t('layout.topbar.language'),
    items: LOCALES.map((locale) => ({
      key: `locale-${locale.code}`,
      label: locale.label,
      checked: appStore.currentLocale === locale.code,
      command: () => appStore.setLocale(locale.code)
    }))
  },
  { separator: true },
  { key: 'logout', label: t('layout.menu.logout'), command: logout }
])
</script>

<template>
  <header
    class="flex h-12 shrink-0 items-center gap-3 border-b border-surface-200 bg-surface-0 px-4 dark:border-surface-800 dark:bg-surface-900"
  >
    <button
      type="button"
      data-test="topbar-menu-button"
      class="-ml-1 flex size-8 items-center justify-center rounded-md text-surface-600 hover:bg-surface-100 lg:hidden dark:text-surface-300 dark:hover:bg-surface-800"
      :aria-label="t('layout.topbar.openMenu')"
      @click="emit('toggleSidebar')"
    >
      <MenuIcon class="size-5" aria-hidden="true" />
    </button>

    <nav data-test="breadcrumb" class="min-w-0 flex-1" :aria-label="t('layout.topbar.breadcrumb')">
      <ol class="flex min-w-0 items-center gap-1.5 text-sm">
        <li
          v-for="(crumb, index) in appStore.breadcrumbs"
          :key="index"
          class="flex min-w-0 items-center gap-1.5"
        >
          <ChevronRightIcon v-if="index > 0" class="size-3.5 shrink-0 text-surface-400" aria-hidden="true" />
          <RouterLink
            v-if="crumb.route"
            :to="{ name: crumb.route }"
            class="truncate text-surface-500 hover:text-surface-900 dark:text-surface-400 dark:hover:text-surface-0"
          >
            {{ crumb.label }}
          </RouterLink>
          <span v-else class="truncate font-medium text-surface-900 dark:text-surface-0">{{ crumb.label }}</span>
        </li>
      </ol>
    </nav>

    <CompanySwitcher />

    <Tag v-if="isAdmin" value="ADMIN" severity="danger" />

    <button
      type="button"
      data-test="user-menu-trigger"
      class="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-contrast focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      :aria-label="t('layout.topbar.userMenu')"
      aria-haspopup="menu"
      @click="userMenu?.toggle($event)"
    >
      {{ initials(userName) }}
    </button>

    <Menu ref="userMenu" :model="menuItems" popup>
      <template #start>
        <div class="border-b border-surface-200 px-3 py-2 dark:border-surface-700">
          <p class="truncate text-sm font-medium text-surface-900 dark:text-surface-0">{{ userName }}</p>
          <p class="truncate text-xs text-surface-500 dark:text-surface-400">{{ userEmail }}</p>
        </div>
      </template>
      <template #item="{ item, props }">
        <a v-bind="props.action" :data-test="`user-menu-${item.key}`" class="flex items-center gap-2">
          <span class="flex-1">{{ item.label }}</span>
          <CheckIcon v-if="item.checked" class="size-4 text-primary" aria-hidden="true" />
        </a>
      </template>
    </Menu>
  </header>
</template>
